import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CircularProgress, Alert } from '@mui/material';

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [allStories, setAllStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedYear, setSelectedYear] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchStories(currentPage);
  }, [currentPage]);

  const fetchStories = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/stories?page=${page}&limit=50`); // Get more stories for filtering
      let fetchedStories = response.data.stories;

      // Extract available years
      const years = [...new Set(fetchedStories.map(story =>
        new Date(story.published_date).getFullYear()
      ))].sort((a, b) => b - a);
      setAvailableYears(years);
      setAllStories(fetchedStories);

      // Apply filters
      applyFilters(fetchedStories, selectedYear, sortOrder);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching stories:', error);
      setError('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (storiesData, year, sort) => {
    let filtered = storiesData;

    // Filter by year
    if (year !== 'all') {
      filtered = filtered.filter(story =>
        new Date(story.published_date).getFullYear() === parseInt(year)
      );
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.published_date);
      const dateB = new Date(b.published_date);
      return sort === 'newest' ? dateB - dateA : dateA - dateB;
    });

    setStories(filtered);
  };

  const handleFilterChange = (year, sort) => {
    setSelectedYear(year);
    setSortOrder(sort);
    applyFilters(allStories, year, sort);
  };

  // Updated date format to dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <CircularProgress />
        <p>Verhalen worden geladen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="stories-page">
      <div className="stories-header">
        <h1>Alle UKV tot nu toe</h1>
        <p className="stories-intro">
          Wie ik ben, wat ik voel en wat ik denk, laat zich wel lezen uit mijn verhaaltjes.
        </p>
      </div>

      <hr className="blue-line" />

      <div className="stories-filters">
        <h2>Alle verhalen op een rij</h2>
        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="year-filter">Jaar:</label>
            <select
              id="year-filter"
              value={selectedYear}
              onChange={(e) => handleFilterChange(e.target.value, sortOrder)}
              className="filter-select"
            >
              <option value="all">Alle jaren</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="sort-filter">Sorteer:</label>
            <select
              id="sort-filter"
              value={sortOrder}
              onChange={(e) => handleFilterChange(selectedYear, e.target.value)}
              className="filter-select"
            >
              <option value="newest">Nieuwste eerst</option>
              <option value="oldest">Oudste eerst</option>
            </select>
          </div>
        </div>
      </div>

      <div className="stories-grid">
        {stories.length === 0 ? (
          <div className="no-stories">
            <p>Geen verhalen gevonden voor de geselecteerde filters.</p>
          </div>
        ) : (
          stories.map((story) => (
            <div key={story.id} className="story-card">
              <h3 className="story-title">{story.title}</h3>
              <p className="story-date">{formatDate(story.published_date)}</p>
              {story.excerpt && (
                <p className="story-excerpt">{story.excerpt}</p>
              )}
              <Link
                to={`/stories/${story.slug}`}
                className="story-read-button"
              >
                lees verder ›
              </Link>
            </div>
          ))
        )}
      </div>

      <div className="stories-footer">
        <Link to="/" className="blue-button centered-button">
          Lees verder
        </Link>
      </div>
    </div>
  );
};

export default Stories;